import { Box } from '@mui/material';
import { AxiosError } from 'axios';
import { Button, Typography } from 'components';
import { TextField } from 'components/Form';
import { Formik } from 'formik';
import React, { useEffect } from 'react';
import {
  HTTPValidationError,
  ModulesTrainingActivitiesSchemasActivitySchema,
  Workflow
} from 'schemas';
import * as Yup from 'yup';

interface IProps {
  config_id: string;
  camera_length: number;
  activities?: ModulesTrainingActivitiesSchemasActivitySchema[];
  workflow_data: Workflow[];
  onSumbit: (model_name: string, epochs: number) => void;
  loading?: boolean;
  error?: AxiosError<HTTPValidationError, any> | null;
  trainingStarted?: boolean;
  videoList?: string[];
  workflowName?: string;
  setWorkFlowName?: any;
}

interface FormValues {
  modal_name: string;
  config_id: string;
  epochs: number;
  workflow_name?: string;
}

const TrainSummaryModal: React.FC<IProps> = ({
  config_id,
  activities,
  videoList = [],
  camera_length,
  onSumbit,
  error,
  trainingStarted,
  workflowName,
  setWorkFlowName
}) => {
  const initialValues: FormValues = {
    modal_name: '',
    config_id: config_id,
    epochs: 300,
    workflow_name: workflowName || ''
  };
  const validationSchema = Yup.object().shape({
    modal_name: Yup.string()
      .required('Model Name is required')
      .min(4, 'Name must be min. 4 characters')
      .max(15, 'Should not exceed 15 characters')
      .matches(/^[^\s]/, 'Name cannot start with a space') // Prevents space at the start
      .matches(
        /^[a-zA-Z0-9][a-zA-Z0-9_\- ]*$/,
        'letter, num, hyphen, _ are allowed'
      ),
    workflow_name: Yup.string()
      .required('Workflow Name is required')
      .min(4, 'Name must be min. 4 characters')
      .max(15, 'Should Not exceed 15 characters')
      .matches(/^[^\s]/, 'Name cannot start with a space') // Prevents space at the start
      .matches(
        /^[a-zA-Z0-9][a-zA-Z0-9_\- ]*$/,
        'letter, num, hyphen, _ are allowed'
      ),
    config_id: Yup.string().required('Config ID is required'),

    epochs: Yup.number()
      .min(1, 'Learning Cycles Must be greater than 0')
      .max(500, 'Learning Cycles be less than 500')
      .typeError('Learning Cycles must be a number')
      .required('Learning Cycles is required')
  });

  return (
    <Box>
      {trainingStarted ? (
        <>
          <Box my={4}>
            <Typography
              variant="h6"
              color="primary"
              textAlign={'center'}
              style={{
                fontWeight: 'bold'
              }}
            >
              Model training has started!
            </Typography>
          </Box>
        </>
      ) : (
        <>
          <Formik<FormValues>
            initialValues={initialValues}
            validateOnBlur={false}
            validateOnChange={false}
            validationSchema={validationSchema}
            onSubmit={(values, { setSubmitting }) => {
              onSumbit(values.modal_name, values.epochs);
              if (setWorkFlowName) {
                setWorkFlowName(values.workflow_name);
              }
              setSubmitting(false);
            }}
          >
            {({ handleSubmit, isSubmitting }) => (
              <form onSubmit={handleSubmit}>
                <div>
                  <table width={'100%'} className="modal-summary-table">
                    <tbody>
                      <tr>
                        <td>
                          <Typography
                            variant="subtitle2"
                            style={{
                              fontWeight: 'bold'
                            }}
                          >
                            No. of Videos
                          </Typography>
                        </td>
                        <td>
                          <Typography variant="subtitle2">
                            {videoList.length || 0}
                          </Typography>
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <Typography
                            variant="subtitle2"
                            style={{
                              fontWeight: 'bold'
                            }}
                          >
                            No. of Activities
                          </Typography>
                        </td>
                        <td>
                          <Typography variant="subtitle2">
                            {activities?.length || 0}
                          </Typography>
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <Typography
                            variant="subtitle2"
                            style={{
                              fontWeight: 'bold'
                            }}
                          >
                            Model Name
                          </Typography>
                        </td>
                        <td>
                          <Typography variant="subtitle2">
                            <TextField
                              name="modal_name"
                              type="text"
                              variant="outlined"
                              size="small"
                              required
                            />
                          </Typography>
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <Typography
                            variant="subtitle2"
                            style={{
                              fontWeight: 'bold'
                            }}
                          >
                            Workflow Name
                          </Typography>
                        </td>
                        <td>
                          <Typography variant="subtitle2">
                            <TextField
                              name="workflow_name"
                              type="text"
                              variant="outlined"
                              size="small"
                              required
                              onChange={(e: any) => setWorkFlowName(e)}
                            />
                          </Typography>
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <div style={{ display: 'flex' }}>
                            {' '}
                            <Typography
                              variant="subtitle2"
                              style={{
                                fontWeight: 'bold'
                              }}
                            >
                              Learning Cycles
                            </Typography>
                          </div>
                        </td>
                        <td>
                          <Typography variant="subtitle2">
                            <TextField
                              name="epochs"
                              type="number"
                              variant="outlined"
                              size="small"
                              onKeyDown={(e: any) => {
                                if (e.key === ' ') {
                                  e.preventDefault();
                                }
                              }}
                              required
                            />
                          </Typography>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                {error && (
                  <Typography variant="subtitle2" color="error">
                    {error.message || ''}
                  </Typography>
                )}
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  mt={3}
                >
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    size="large"
                    style={{
                      backgroundColor: '#295D65'
                    }}
                    disabled={isSubmitting}
                  >
                    Start Training
                  </Button>
                </Box>
              </form>
            )}
          </Formik>
        </>
      )}
    </Box>
  );
};

export default TrainSummaryModal;
